import type { MapProviderType } from 'src/types/map-provider';

/**
 * Map provider configuration
 * Handles environment variables and provides type-safe configuration
 */
export interface MapConfig {
  
  defaultProvider: MapProviderType | 'auto';
  enableUniversalMaps: boolean;
  enableBaiduMaps: boolean;
  enableOpenStreetMap: boolean;
  
  
  googleMapsApiKey?: string;
  googleMapsMapId?: string;
  baiduMapsApiKey?: string;
  baiduMapsSecretKey?: string;
  
  
  
  countryDetection: {
    timeout: number;
    useTimezone: boolean;
    useLanguage: boolean;
    cacheDuration: number;
  };
  
  
  autocomplete: {
    maxResults: number;
    debounceMs: number;
  };
  
  
  dynamicImports: boolean;
  
  
  showDebugInfo: boolean;
  enableLogging: boolean;
}

/**
 * Get map configuration from environment variables
 */
function getMapConfig(): MapConfig {
  const envProvider = process.env.NEXT_PUBLIC_DEFAULT_MAP_PROVIDER;
  let defaultProvider: MapProviderType | 'auto' = 'google'; 
  
  
  if (envProvider === 'auto' || envProvider === 'google' || envProvider === 'baidu' || envProvider === 'openstreetmap') {
    defaultProvider = envProvider;
  } else if (envProvider) {
    console.warn(`Invalid NEXT_PUBLIC_DEFAULT_MAP_PROVIDER: ${envProvider}. Falling back to 'auto'.`);
    defaultProvider = 'auto'; 
  }

  return {
    
    defaultProvider,
    enableUniversalMaps: process.env.NEXT_PUBLIC_ENABLE_UNIVERSAL_MAPS === 'true',
    enableBaiduMaps: process.env.NEXT_PUBLIC_ENABLE_BAIDU_MAPS !== 'false', 
    enableOpenStreetMap: process.env.NEXT_PUBLIC_ENABLE_OPENSTREETMAP !== 'false', 
    
    
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    googleMapsMapId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID,
    baiduMapsApiKey: process.env.NEXT_PUBLIC_BAIDU_MAPS_API_KEY,
    baiduMapsSecretKey: process.env.NEXT_PUBLIC_BAIDU_MAPS_SECRET_KEY,
    
    
    countryDetection: {
      timeout: parseInt(process.env.NEXT_PUBLIC_COUNTRY_DETECTION_TIMEOUT || '5000', 10),
      useTimezone: process.env.NEXT_PUBLIC_COUNTRY_DETECTION_USE_TIMEZONE !== 'false',
      useLanguage: process.env.NEXT_PUBLIC_COUNTRY_DETECTION_USE_LANGUAGE !== 'false',
      cacheDuration: parseInt(process.env.NEXT_PUBLIC_COUNTRY_CACHE_DURATION || '3600000', 10), 
    },
    
    
    autocomplete: {
      maxResults: parseInt(process.env.NEXT_PUBLIC_MAP_AUTOCOMPLETE_MAX_RESULTS || '10', 10),
      debounceMs: parseInt(process.env.NEXT_PUBLIC_MAP_AUTOCOMPLETE_DEBOUNCE || '300', 10),
    },
    
    
    dynamicImports: process.env.NEXT_PUBLIC_MAP_DYNAMIC_IMPORTS !== 'false', 
    
    
    showDebugInfo: process.env.NODE_ENV === 'development' && 
                   process.env.NEXT_PUBLIC_SHOW_MAP_PROVIDER_DEBUG === 'true',
    enableLogging: process.env.NODE_ENV === 'development' &&
                   process.env.NEXT_PUBLIC_MAP_DEBUG_LOGGING === 'true',
  };
}

/**
 * Global map configuration instance
 */
export const mapConfig: MapConfig = getMapConfig();

/**
 * Get API keys for all providers
 */
export function getApiKeys(): Partial<Record<MapProviderType, string>> {
  const keys: Partial<Record<MapProviderType, string>> = {};
  
  if (mapConfig.googleMapsApiKey) {
    keys.google = mapConfig.googleMapsApiKey;
  }
  
  if (mapConfig.baiduMapsApiKey) {
    keys.baidu = mapConfig.baiduMapsApiKey;
  }
  
  return keys;
}

/**
 * Validate map configuration
 */
export function validateMapConfig(): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  
  if (mapConfig.enableUniversalMaps) {
    const hasGoogleKey = !!mapConfig.googleMapsApiKey;
    const hasBaiduKey = !!mapConfig.baiduMapsApiKey;
    const hasOpenStreetMap = mapConfig.enableOpenStreetMap;
    
    if (!hasGoogleKey && !hasBaiduKey && !hasOpenStreetMap) {
      errors.push('Universal maps are enabled but no providers are configured');
    }
    
    
    if (mapConfig.defaultProvider === 'google' && !hasGoogleKey) {
      errors.push('Google Maps is set as default provider but NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is missing');
    }
    
    if (mapConfig.defaultProvider === 'baidu' && !hasBaiduKey) {
      errors.push('Baidu Maps is set as default provider but NEXT_PUBLIC_BAIDU_MAPS_API_KEY is missing');
    }

    if (mapConfig.defaultProvider === 'openstreetmap' && !hasOpenStreetMap) {
      warnings.push('OpenStreetMap is set as default provider but is disabled');
    }
    
    if (mapConfig.defaultProvider === 'auto') {
      if (!hasGoogleKey) {
        warnings.push('Google Maps API key is missing - auto provider selection may fail for non-China users');
      }
      
      if (!hasBaiduKey && mapConfig.enableBaiduMaps) {
        warnings.push('Baidu Maps API key is missing - auto provider selection may fail for China users');
      }
      
      if (!hasOpenStreetMap) {
        warnings.push('OpenStreetMap is disabled - fallback options are limited');
      }
    }
  }
  
  
  if (mapConfig.countryDetection.timeout < 1000) {
    warnings.push('Country detection timeout is very low, consider increasing it');
  }
  
  if (mapConfig.autocomplete.debounceMs < 100) {
    warnings.push('Autocomplete debounce is very low, may cause excessive API calls');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate configuration status (development only)
 */
export function logConfigStatus(): void {
  if (process.env.NODE_ENV !== 'development') return;
  
  const validation = validateMapConfig();
  
  
  
}


if (process.env.NODE_ENV === 'development') {
  logConfigStatus();
}

export default mapConfig;
